import { useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n.jsx';
import { api } from '../api.js';

/**
 * Estado de frescura de los datos, con actualizacion manual.
 *
 * El servidor ya se actualiza solo (cartas semanal, meta diario), asi que esto
 * es sobre todo una ventana para VER que los datos estan al dia -- y un boton
 * para forzarlo cuando acaba de salir una expansion y no quieres esperar al
 * proximo ciclo.
 *
 * Mientras algo corre se consulta el avance cada 3 s; el resto del tiempo no se
 * consulta nada, para no tener una peticion de fondo eterna sin motivo.
 */

const HOUR = 3600_000;

function freshness(job) {
  if (job.running) return 'running';
  if (job.ageMs == null) return 'never';
  return job.ageMs > job.everyMs ? 'stale' : 'fresh';
}

export default function RefreshStatus() {
  const { lang, t } = useLang();
  const [jobs, setJobs] = useState(null);
  const [open, setOpen] = useState(false);
  const [denied, setDenied] = useState(false);

  /*
   * El sondeo anterior, para detectar el FINAL de un trabajo. Cuando algo que
   * corria deja de correr, los datos de la base acaban de cambiar: se avisa a
   * las vistas (ptcg:datos-frescos) para que vuelvan a pedir, en vez de dejar
   * la tabla vieja en pantalla hasta que alguien recargue la pagina a mano.
   * En ref y no en estado: es memoria del sondeo, no algo que pintar.
   */
  const previos = useRef(null);

  const load = () =>
    api('/api/refresh/status')
      .then((r) => r.json())
      .then((d) => {
        const nuevos = d.jobs ?? [];
        const termino = previos.current?.some(
          (p) => p.running && nuevos.find((n) => n.name === p.name)?.running === false
        );
        previos.current = nuevos;
        if (termino) window.dispatchEvent(new CustomEvent('ptcg:datos-frescos'));
        setJobs(nuevos);
      })
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  /*
   * SE CIERRA AL PINCHAR FUERA, y con Escape.
   *
   * Solo se cerraba volviendo a pulsar "Datos", asi que en cuanto pinchabas
   * cualquier otra cosa el panel se quedaba abierto tapando la pagina. Todo lo
   * que se despliega en esta casa se cierra asi.
   *
   * `pointerdown` y no `click`: el clic llega DESPUES de que el elemento de
   * debajo haya reaccionado, y ademas un arrastre que empieza dentro y termina
   * fuera no dispara `click` -- el panel se quedaba abierto al seleccionar
   * texto. Se comprueba contra la caja entera, no contra el panel: pulsar el
   * propio boton ya alterna, y cerrarlo aqui tambien lo dejaria sin efecto.
   */
  const caja = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const fuera = (e) => {
      if (!caja.current?.contains(e.target)) setOpen(false);
    };
    const escape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', fuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', fuera);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const anyRunning = jobs?.some((j) => j.running);
  useEffect(() => {
    if (!anyRunning && !open) return;
    const id = setInterval(load, anyRunning ? 3000 : 15000);
    return () => clearInterval(id);
  }, [anyRunning, open]);

  const start = async (name) => {
    const r = await api(`/api/refresh/${name}`, { method: 'POST' });
    /*
     * 401 = sin sesión. 403 = con sesión pero sin ser administrador.
     *
     * Se dice en vez de fallar en silencio, que es lo que pasaba antes con
     * cualquier error de esta llamada. El caso realista ahora es que la sesión
     * haya caducado mientras el panel estaba abierto: el botón parecería no
     * hacer nada.
     */
    setDenied(r.status === 401 || r.status === 403);
    load();
  };

  if (!jobs) return null;

  const worst = jobs.some((j) => j.running)
    ? 'running'
    : jobs.some((j) => freshness(j) !== 'fresh')
      ? 'stale'
      : 'fresh';

  const ageText = (job) => {
    if (job.ageMs == null) return t('never');
    const h = job.ageMs / HOUR;
    if (h < 1) return t('justNow');
    if (h < 48) return `${Math.round(h)} h`;
    return `${Math.round(h / 24)} ${t('daysAgo')}`;
  };

  return (
    <div className="refresh" ref={caja}>
      <button
        type="button"
        className={`refresh__toggle is-${worst}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={t('dataStatus')}
      >
        <span className="refresh__dot" />
        {t('data')}
      </button>

      {open && (
        <div className="refresh__panel">
          <p className="refresh__intro muted">{t('autoNote')}</p>
          {jobs.map((j) => (
            <div className="refresh__job" key={j.name}>
              <div className="refresh__jobinfo">
                <span className={`refresh__dot is-${freshness(j)}`} />
                <div>
                  <strong>{j.label}</strong>
                  <span className="muted"> · {ageText(j)}</span>
                  {/* Si la última corrida falló, se DICE: un job que falla nunca
                      avanza su edad, así que sin esto se quedaba viejo y mudo
                      (pasó con meta, atascado 4 días). */}
                  {!j.running && j.result && j.result !== 'ok' && (
                    <span className="refresh__err"> · {t('lastRunFailed')}</span>
                  )}
                  {j.running && j.progress && (
                    <p className="refresh__progress muted">{j.progress}</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={j.running}
                onClick={() => start(j.name)}
              >
                {j.running ? t('updating') : t('updateNow')}
              </button>
            </div>
          ))}

          {denied && <p className="refresh__denied">{t('refreshDenied')}</p>}
        </div>
      )}
    </div>
  );
}
